import React, { useState } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import { Modal, ModalButtonGroup } from 'js/design_system/Modal';
import { TextButton } from 'js/design_system/TextButton';
import type { WorkId } from 'js/liswp/types';
import FeedbackOption from './feedback_option';
import { t } from 'js/intl/t';
import 'js/i18n/page_translations/work_page_modals/en.wjs';

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export interface ReportModalProps {
  reportWork: (choices: string[], workId: WorkId, category: string) => void;
  workId: WorkId;
  onClose: () => void;
}

const ReportModal: React.FC<ReportModalProps> = ({
  reportWork,
  workId,
  onClose,
}) => {
  const [choices, setChoices] = useState<string[]>([]);

  const recordChoice = (choice: string) => {
    if (choices.includes(choice)) {
      setChoices(choices.filter((c) => c !== choice));
    } else {
      setChoices([...choices, choice]);
    }
  };

  const onSubmit = () => {
    if (choices.length === 0) {
      return;
    }

    reportWork(choices, workId, 'general');
    onClose();
  };

  const onCancel = () => {
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('work_page_modals.report_modal.title')}
    >
      <Body style={{ marginBottom: 16 }}>
        {t('work_page_modals.report_modal.body')}
      </Body>
      <Options>
        <FeedbackOption
          onClick={() => recordChoice('spam')}
          iconName="fa-flag"
          color="red"
          text={t(
            'work_page_modals.report_modal.options.inappropriate_or_spam',
          )}
          selected={choices.includes('spam')}
        />
        <FeedbackOption
          onClick={() => recordChoice('copyrighted_material')}
          iconName="fa-ban"
          color="brown"
          text={t('work_page_modals.report_modal.options.copyrighted_material')}
          selected={choices.includes('copyrighted_material')}
        />
        <FeedbackOption
          onClick={() => recordChoice('uploader_not_author')}
          iconName="fa-user-slash"
          color="yellow"
          text={t('work_page_modals.report_modal.options.uploader_not_author')}
          selected={choices.includes('uploader_not_author')}
        />
        <FeedbackOption
          onClick={() => recordChoice('other')}
          iconName="fa-question"
          color="gray"
          text={t('work_page_modals.report_modal.options.something_else')}
          selected={choices.includes('other')}
        />
      </Options>
      <ModalButtonGroup>
        <TextButton size="sm" onClick={onCancel}>
          {t('work_page_modals.report_modal.cancel')}
        </TextButton>
        <Button size="sm" disabled={choices.length === 0} onClick={onSubmit}>
          {t('work_page_modals.report_modal.submit')}
        </Button>
      </ModalButtonGroup>
    </Modal>
  );
};

export default ReportModal;
